// ── CallRegister 이펙트 + fetch + 리사이즈 훅 ────────────────────────────────
// - resize / column drag useEffect
// - 초기 데이터 fetch, 외부직원 ticket_type 자동 설정, 결과 텍스트 타이머
// - 다음 영업일 자동 설정, 관리자 담당자 자동 선택, textarea 높이 (담당자 칸 후보는 서버 call-owners — 2026-10-23)
// - 조회 API 6개는 300줄 규칙으로 useCallRegisterFetch.js 로 분리(2026-09-20), 여기서 합출력만 한다
// - onResizeStart, holidaySet, nextBizDate

import { useEffect, useRef } from 'react';
import { MIN_COL_WEIGHT } from './constants';
import {
  getColumnMinPx, minWeightFromPx,
} from './utils';
import { holidaySet as _holidaySet, nextBizDate as _nextBizDate } from './useCallRegisterBizDate';
import { createCallRegisterFetchers } from './useCallRegisterFetch';

// integrated: 통합 일일콜 메뉴(2026-10-13) — companyCode 는 화면에서 고른 회사(바뀌면 상품을 다시 읽는다)
export function useCallRegisterHandlers(core, { adminMode, adminApiBase, scope, companyCode, showToast, integrated = false }) {
  // 조회 API 가 쓰던 setter 들은 useCallRegisterFetch.js 로 함께 옮겨갔다(2026-09-20).
  const {
    setIsMobile, setForm,
    setSubmitResultText,
    form,
    externalCategoryName: currentExternalCategoryName,
    isExternalWorker,
    editForm, editOpen,
    formContentRef, editContentRef,
    resizeRef, holidayRef,
    setSharedColWidths,
    sharedColWidths,
  } = core;

  // isMovieAdminMode는 CallRegister에서 계산해 core에 없으므로 props로 직접 계산
  const isMovieModeLocal = adminMode && Boolean(companyCode);

  // 조회 API 6개 (useCallRegisterFetch.js) — 아래 이펙트들이 바로 쓰므로 먼저 만든다.
  const fetchers = createCallRegisterFetchers(core, { adminMode, adminApiBase, scope, companyCode, showToast, integrated });
  const { fetchOptions, fetchMyRole, fetchSalesPersons, fetchPrefs, fetchList, fetchOwnerOptions } = fetchers;

  // ── resize 이펙트 ──────────────────────────────────────────────────────────
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── 컬럼 드래그 리사이즈 mousemove/mouseup 이펙트 ─────────────────────────
  useEffect(() => {
    const onMouseMove = (e) => {
      const state = resizeRef.current;
      if (!state) return;
      const delta = e.clientX - state.startX;
      const unitWidth = Math.max(320, state.viewportWidth || window.innerWidth);
      const deltaWeight = (delta / unitWidth) * state.startTotal;
      const nextWeight = Math.max(state.minWeight || MIN_COL_WEIGHT, state.startWidth + deltaWeight);
      setSharedColWidths((prev) => {
        const next = [...prev];
        next[state.index] = nextWeight;
        return next;
      });
    };
    const onMouseUp = () => {
      if (!resizeRef.current) return;
      resizeRef.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── 마운트 시 초기 데이터 조회 ─────────────────────────────────────────────
  useEffect(() => {
    fetchOptions();
    fetchMyRole();
    fetchSalesPersons();
    fetchPrefs();
    fetchList();
    fetchOwnerOptions(); // 관리 화면만 — 담당자 칸 후보(재직 + 일일콜 열쇠, 2026-10-23)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── 통합 메뉴: 등록할 회사를 바꾸면 상품 칸을 비우고 그 회사 상품을 다시 읽는다 ──
  //   비우지 않으면 상품이 0개인 회사로 바꿨을 때 옛 회사 분류 글자가 남아 다른 회사 콜로 저장될 수 있다.
  const firstCompanyRun = useRef(true);
  useEffect(() => {
    if (!integrated) return;
    if (firstCompanyRun.current) { firstCompanyRun.current = false; return; } // 처음 한 번은 위 마운트 조회가 한다
    setForm((prev) => ({ ...prev, ticket_type_id: null, ticket_type: '' }));
    fetchOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integrated, companyCode]);

  // ── 외부직원/영화관리/companyCode: 카테고리명 로드 시 ticket_type_id 고정값 자동 설정
  // 외부 회사 카테고리명 자동 설정
  // 상품명이 카테고리명과 일치하면 해당 ticket_type_id 설정
  // 일치하는 상품이 없으면 카테고리명 자체를 표시용으로 쓰는 것이므로 ticket_type_id = null
  useEffect(() => {
    if (!currentExternalCategoryName) return;
    if (!isExternalWorker && !isMovieModeLocal && !companyCode) return;
    const match = core.ticketTypes.find((t) => t.name === currentExternalCategoryName);
    if (match) {
      // 상품명 일치 → 해당 id 저장
      setForm((prev) => ({ ...prev, ticket_type_id: match.id }));
    } else {
      // 카테고리명 표시 모드 (예: "영화티켓") → ticket_type_id null, ticket_type에 카테고리명 저장
      setForm((prev) => ({ ...prev, ticket_type_id: null, ticket_type: currentExternalCategoryName }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentExternalCategoryName, isExternalWorker, isMovieModeLocal, companyCode, core.ticketTypes]);

  // ── 등록 성공 텍스트 10초 후 자동 제거 ────────────────────────────────────
  useEffect(() => {
    const text = core.submitResultText;
    if (!text) return undefined;
    const timer = setTimeout(() => setSubmitResultText(''), 10000);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [core.submitResultText]);

  // ── 초기 다음 영업일 자동 설정 ────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      const d = await nextBizDate();
      setForm((p) => (p.visit_date ? p : { ...p, visit_date: d }));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── 관리자 담당자 자동 선택: ownerOptions 로드 후 첫 번째 항목 선택 ────────
  useEffect(() => {
    if (!adminMode) return;
    if (!core.ownerOptions.length) return;
    setForm((prev) => {
      if (prev.owner_user_id) return prev;
      return { ...prev, owner_user_id: String(core.ownerOptions[0].id) };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminMode, core.ownerOptions]);

  // ── 등록 폼 내용 textarea 자동 높이 확장 ──────────────────────────────────
  useEffect(() => {
    const el = formContentRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.content]);

  // ── 수정 모달 내용 textarea 자동 높이 확장 ────────────────────────────────
  useEffect(() => {
    const el = editContentRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editForm.content, editOpen]);

  // (2026-10-23) 예전엔 여기서 콜 목록의 '콜 건 사람'으로 담당자 칸을 만들었다 — 퇴사자가 남고 콜 0건 회사는 칸이 비었다.
  //   이제 모든 관리 화면이 서버 후보(재직 + 일일콜 열쇠)를 받는다(위 마운트 조회 fetchOwnerOptions).

  // ── 컬럼 리사이즈 시작 핸들러 ─────────────────────────────────────────────
  function onResizeStart(index, event) {
    if (!Number.isInteger(index) || index < 0) return;
    event.preventDefault();
    event.stopPropagation();
    const source = sharedColWidths;
    const startTotal = source.reduce((sum, v) => sum + (Number(v) || 0), 0);
    const viewportWidth = window.innerWidth;
    const minPx = getColumnMinPx(index);
    const minWeight = Math.max(MIN_COL_WEIGHT, minWeightFromPx(minPx, viewportWidth, startTotal));
    resizeRef.current = {
      index,
      startX: event.clientX,
      startWidth: source[index] || MIN_COL_WEIGHT,
      startTotal,
      viewportWidth,
      minWeight,
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }

  // ── 공휴일/다음영업일 — holidayRef를 바인딩한 래퍼 ─────────────────────────
  // 실제 로직은 useCallRegisterBizDate.js에 있음
  const holidaySet  = (y) => _holidaySet(y, holidayRef);
  const nextBizDate = ()  => _nextBizDate(holidayRef);

  return {
    onResizeStart,
    holidaySet,
    nextBizDate,
    ...fetchers,
  };
}
